import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check, Globe, Sparkles, ShieldCheck, Workflow } from "lucide-react";

const tools = [
  {
    name: "Framer / Framer AI",
    fit: "Designer-grade portfolios, animations, no-code",
    why:
      "Best-in-class motion, beautiful templates, easy custom domain mapping. Excellent for a premium personal brand site.",
  },
  {
    name: "Webflow + Webflow AI",
    fit: "Designer-grade marketing sites with strong CMS",
    why:
      "Industry standard for high-end design agencies. Native CMS makes the blog effortless. Generous SEO controls.",
  },
  {
    name: "Wix Studio",
    fit: "AI-assisted, fast, low overhead",
    why:
      "Built-in AI site builder, integrated booking, forms and email marketing. Great if you want everything in one place.",
  },
  {
    name: "Squarespace + Squarespace AI",
    fit: "Editorial-grade typography, simple CMS",
    why:
      "Beautiful out-of-the-box typography. Strong for trainers who want to focus on content over engineering.",
  },
  {
    name: "Durable / 10Web / Hostinger AI",
    fit: "Generate-a-site-in-minutes",
    why:
      "Useful for fast first drafts. Limited in advanced animation and custom interactions.",
  },
  {
    name: "Custom (this site)",
    fit: "Full-stack, AI-assisted, fully owned",
    why:
      "What you're seeing now — Cabinet Grotesk + Satoshi typography, framer-motion animations, custom AI chatbot, admin CMS. Built and owned end-to-end.",
  },
];

export default function AdvisoryPage() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="advisory-page">
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-white/30" />
            <span className="label-eyebrow">Advisory</span>
          </div>
          <h1 className="font-display font-bold text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
            Domain & tooling <br />
            <span className="text-gradient">recommendations.</span>
          </h1>
          <p className="mt-6 text-slate-400 text-lg max-w-2xl leading-relaxed">
            A short, opinionated brief on which domain to lead with and the AI
            site-builders worth your attention as you scale your personal brand.
          </p>

          {/* Domain section */}
          <section className="mt-16">
            <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-white mb-6">
              <Globe size={20} className="inline-block mr-2 text-blue-400 -mt-1" />
              aksahni.com vs. techsahni.in
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Card highlight title="aksahni.com" subtitle="Recommended primary brand">
                <Row good>Short, memorable, premium-sounding (4 letters + .com)</Row>
                <Row good>Globally neutral TLD — works in EU, US, GCC, APAC equally</Row>
                <Row good>Personal brand stays evergreen as your focus areas evolve</Row>
                <Row good>Stronger SEO equity over time on a .com</Row>
                <Row good>Better for global enterprise procurement and trust</Row>
              </Card>

              <Card title="techsahni.in" subtitle="Use as a redirect / India-only landing">
                <Row>Descriptive of tech focus, but locks you into one vertical</Row>
                <Row>.in TLD signals India-localized service to non-Indian buyers</Row>
                <Row>Makes scope expansion (advisory, GenAI, leadership coaching) harder</Row>
                <Row good>Useful as a 301-redirect to aksahni.com to capture all traffic</Row>
              </Card>
            </div>

            <div className="mt-8 p-6 rounded-2xl border border-blue-500/30 bg-blue-600/5">
              <div className="font-display font-semibold text-white text-lg">
                The verdict
              </div>
              <p className="mt-2 text-slate-300 leading-relaxed">
                Lead with <span className="text-white font-medium">aksahni.com</span> —
                it's shorter, more authoritative and globally portable. Park{" "}
                <span className="text-white font-medium">techsahni.in</span> with a
                permanent 301 redirect to <span className="text-white">aksahni.com</span> so
                you keep ownership of the search term while consolidating SEO equity
                under a single brand.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-400">
                <li className="flex gap-2"><Check size={14} className="mt-1 text-blue-400" /> Set aksahni.com as canonical</li>
                <li className="flex gap-2"><Check size={14} className="mt-1 text-blue-400" /> 301 → techsahni.in to aksahni.com at the registrar / CDN</li>
                <li className="flex gap-2"><Check size={14} className="mt-1 text-blue-400" /> Use connect@aksahni.com for all outbound communication</li>
                <li className="flex gap-2"><Check size={14} className="mt-1 text-blue-400" /> Reserve LinkedIn, GitHub, X, YouTube handles as <span className="font-mono">@asahni</span> or <span className="font-mono">@ajaysahni</span></li>
              </ul>
            </div>
          </section>

          {/* Tools section */}
          <section className="mt-20">
            <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-white mb-6">
              <Sparkles size={20} className="inline-block mr-2 text-blue-400 -mt-1" />
              AI site-builders worth your attention
            </h2>
            <p className="text-slate-400 max-w-2xl">
              Mapped to your needs: animations, certification showcases,
              testimonials, courses, booking, blog and a corporate aesthetic.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {tools.map((t) => (
                <div
                  key={t.name}
                  className="bg-[#0A0C13] border border-white/10 rounded-2xl p-6 hover:border-white/25 transition-colors"
                >
                  <div className="font-display font-semibold text-white text-lg">{t.name}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-blue-300">
                    {t.fit}
                  </div>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t.why}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Architecture */}
          <section className="mt-20">
            <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-white mb-4">
              <Workflow size={20} className="inline-block mr-2 text-blue-400 -mt-1" />
              Recommended stack for aksahni.com
            </h2>
            <ul className="mt-4 space-y-2 text-slate-300 text-sm">
              <li className="flex gap-2"><ShieldCheck size={14} className="mt-1 text-emerald-400" />Custom React + FastAPI + MongoDB (this site) — fully owned, future-proof</li>
              <li className="flex gap-2"><ShieldCheck size={14} className="mt-1 text-emerald-400" />Cloudflare in front (CDN, WAF, DDoS) — free tier is enough</li>
              <li className="flex gap-2"><ShieldCheck size={14} className="mt-1 text-emerald-400" />Resend for transactional email; Calendly/Cal.com embed for booking</li>
              <li className="flex gap-2"><ShieldCheck size={14} className="mt-1 text-emerald-400" />Plausible / PostHog for privacy-friendly analytics</li>
              <li className="flex gap-2"><ShieldCheck size={14} className="mt-1 text-emerald-400" />MX + SPF/DKIM/DMARC on aksahni.com so connect@aksahni.com lands in inboxes</li>
            </ul>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Card({ title, subtitle, highlight, children }) {
  return (
    <div
      className={`relative bg-[#0A0C13] rounded-2xl p-6 border ${
        highlight ? "border-blue-500/40 glow-blue" : "border-white/10"
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 left-6 text-[10px] font-mono uppercase tracking-[0.22em] bg-blue-600 text-white px-3 py-1 rounded-full">
          Recommended
        </span>
      )}
      <div className="font-display font-bold text-white text-2xl">{title}</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
        {subtitle}
      </div>
      <ul className="mt-5 space-y-2.5">{children}</ul>
    </div>
  );
}

function Row({ children, good }) {
  return (
    <li className="flex items-start gap-2 text-sm text-slate-300">
      <span
        className={`mt-1 h-1.5 w-1.5 rounded-full ${good ? "bg-emerald-400" : "bg-amber-400"}`}
      />
      <span>{children}</span>
    </li>
  );
}
